import React from 'react';
import { useQuery } from '@apollo/client';
import { Truck } from 'lucide-react';
import iso3166 from 'iso3166';
import {
  DeliveryProfile,
  DeliveryRate,
  DeliveryRateType,
} from '@shopify-multiseller/graphql/partner/types';
import { AdminUI } from '../../ui/AdminUI';
import { SaveBarBlocker } from '../../context/SaveBarBlocker';
import { CommandHeader } from '../../components/CommandHeader';
import { SectionCard } from '../../components/SectionCard';
import { EmptyState } from '../../components/EmptyState';
import { Button, Badge } from '../../components/primitives';
import { Spinner } from '../../components';
import {
  useDeliveryProfile,
  DeliveryProfileProvider,
} from '../../components/DeliveryProfileEditor';
import { SHIPPING_PROFILES_QUERY } from './queries';
import { formatCurrency } from '../../helpers/formatters';
import { useShopCurrency } from '../../hooks';

type ProfilesData = { deliveryProfiles: DeliveryProfile[] };

const countryName = (code?: string | null): string | undefined => {
  if (!code) return undefined;
  const entry = (iso3166 as Record<string, { name: string }>)[code];
  return entry?.name ?? code;
};

const processingTimeText = (profile: DeliveryProfile): string | undefined => {
  const { min, max, unit } = profile.processingTime ?? {};
  if (min == null && max == null) return undefined;
  const unitText = String(unit ?? '').toLowerCase() || 'days';
  return min === max ? `${min} ${unitText}` : `${min}–${max} ${unitText}`;
};

/**
 * What a rate costs, in words.
 *
 * Free delivery carries no amount, so the card used to print an em dash for
 * it — the same thing it printed for a rate that had never been set. A seller
 * shipping for nothing and a seller who had not finished setting up looked
 * identical.
 */
const rateText = (rate: DeliveryRate | null | undefined, currencyCode: string): string => {
  if (!rate) return '—';
  if (rate.type === DeliveryRateType.FreeDelivery) return 'Free';
  return rate.rate != null ? formatCurrency({ amount: rate.rate, currencyCode }) : '—';
};

/**
 * One fact about a profile, in the row of facts along the top of its card.
 *
 * An em dash for an absent rate was ambiguous — it read the same whether the
 * seller had not set one or had chosen not to ship there at all — so the two
 * are worded apart at the call site.
 */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-sm font-medium tabular-nums text-foreground">{children}</p>
  </div>
);

/**
 * The delivery profiles a seller can apply to their products.
 *
 * The Shipping item has been in the navigation the whole time pointing at
 * /delivery, which is not a route — it fell through to the 404. Profiles were
 * only reachable from inside the product editor, so a seller could not review
 * or fix the rates they had set up without opening a product first.
 */
const ShippingProfiles: React.FC = () => {
  const currencyCode = useShopCurrency();
  const deliveryProfile = useDeliveryProfile();
  const { data, loading, refetch } = useQuery<ProfilesData>(SHIPPING_PROFILES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const profiles = data?.deliveryProfiles ?? [];

  const openEditor = (profileId?: string) => {
    deliveryProfile.openEditor({
      profileId,
      // The list has to reflect the edit; the editor owns the mutation.
      onSave: () => {
        deliveryProfile.closeEditor();
        void refetch();
      },
    });
  };

  return (
        <div data-testid="shipping-page" className="min-h-screen">
          <CommandHeader
            title="Shipping"
            subtitle="Delivery profiles you can apply to your products"
            actions={
              <Button data-testid="add-delivery-profile" onClick={() => openEditor()}>
                Add profile
              </Button>
            }
          />

          <div className="mx-auto max-w-7xl space-y-5 px-6 py-5">
            {loading && profiles.length === 0 && <Spinner size="small" />}

            {!loading && profiles.length === 0 && (
              <EmptyState
                icon={Truck}
                title="No delivery profiles yet"
                description="A profile holds your origin, processing time and rates, and can be applied to any number of products."
                action={{ label: 'Add profile', onAction: () => openEditor() }}
              />
            )}

            {profiles.map((profile) => (
              <SectionCard
                key={profile.id}
                title={profile.name || 'Untitled profile'}
                testId="delivery-profile-card"
                trailing={
                  <>
                    {/* What a seller checks before changing rates: how much of
                        their catalogue this is about to move. */}
                    <span data-testid="delivery-profile-usage">
                      {profile.productCount === 1
                        ? 'Used by 1 product'
                        : `Used by ${profile.productCount ?? 0} products`}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="edit-delivery-profile"
                      onClick={() => openEditor(profile.id)}
                    >
                      Edit
                    </Button>
                  </>
                }
              >
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Fact label="Ships from">
                    {[countryName(profile.originCountryCode), profile.originPostalCode]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </Fact>
                  <Fact label="Processing time">{processingTimeText(profile) ?? '—'}</Fact>
                  <Fact label="Domestic">{rateText(profile.default, currencyCode)}</Fact>
                  <Fact label="International">
                    {/* The international rate was not on this card at all, so a
                        profile that ships abroad and one that does not looked
                        identical until you opened the editor. */}
                    {profile.otherInternational ? (
                      rateText(profile.otherInternational, currencyCode)
                    ) : (
                      <span className="font-normal text-muted-foreground">
                        {profile.zones && profile.zones.length > 0
                          ? 'Listed countries only'
                          : 'Not shipped'}
                      </span>
                    )}
                  </Fact>
                </div>

                {profile.zones && profile.zones.length > 0 && (
                  <div className="mt-4 border-t border-border/60 pt-3">
                    <p className="mb-1.5 text-xs text-muted-foreground">Zones</p>
                    <div className="flex flex-wrap gap-1.5">
                      {/* A zone has no name of its own — it is the countries
                          it covers and the rate charged to reach them. */}
                      {profile.zones.map((zone) => (
                        <Badge variant="neutral" key={zone.id}>
                          {zone.countries.map((country) => countryName(country.code)).join(', ')}
                          {` · ${rateText(zone.rate, currencyCode)}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </SectionCard>
            ))}
          </div>
        </div>
  );
};

/**
 * The editor lives behind a provider that was only ever mounted inside the
 * product editor, so this page brings its own.
 */
export const Shipping: React.FC = () => (
  <AdminUI>
    <SaveBarBlocker>
      <DeliveryProfileProvider>
        <ShippingProfiles />
      </DeliveryProfileProvider>
    </SaveBarBlocker>
  </AdminUI>
);
